import { Button, Card, CardContent, Stack } from '@mui/material';
import { useChatActions } from '@mui/x-chat/headless';
import type { AgenticPlatformAskUserQuestion } from 'agentic-platform-schema';
import { useState } from 'react';

import { Text, isTruthy } from 'common';

import { AgenticChatQuestionField } from './Field';
import { type AgenticChatQuestionAnswers, formatAgenticChatQuestionAnswer } from './answer';

/** A lone set of choices is answered by the click that picks one, so it needs no submit button. */
type AgenticChatQuestionSubmissionMode = 'SubmitWhenPicked' | 'SubmitWhenSent';

function getAgenticChatQuestionSubmissionMode(
  question: AgenticPlatformAskUserQuestion
): AgenticChatQuestionSubmissionMode {
  return question.fields.length === 1 && question.fields[0]?.kind === 'singleChoice'
    ? 'SubmitWhenPicked'
    : 'SubmitWhenSent';
}

interface AgenticChatQuestionCardProps {
  /** Whether the question is still the agent's open question, rather than one already answered. */
  isOpen: boolean;
  question: AgenticPlatformAskUserQuestion;
}

/** A question the agent asked, rendered inline in the transcript as the form it asked for. */
export function AgenticChatQuestionCard(props: AgenticChatQuestionCardProps) {
  const { isOpen, question } = props;
  const chatActions = useChatActions();
  // A field the agent named `toString` would read back off `Object.prototype`.
  const [answers, setAnswers] = useState<AgenticChatQuestionAnswers>(new Map());
  const submissionMode = getAgenticChatQuestionSubmissionMode(question);
  const answerText = formatAgenticChatQuestionAnswer({ answers, question });

  return (
    <Card variant="outlined" sx={{ marginTop: 0.5 }}>
      <CardContent sx={{ '&, &:last-child': { padding: 1.5 } }}>
        <Stack
          component="form"
          gap={2}
          onSubmit={(event) => {
            event.preventDefault();
            // Enter reaches this without passing the send button, so its rule lives here too.
            if (!isOpen || !isTruthy(answerText.length)) {
              return;
            }
            void chatActions.sendMessage({ parts: [{ text: answerText, type: 'text' }] });
          }}
        >
          <Text variant="body2" bold>
            {question.question}
          </Text>
          {question.fields.map((field) => (
            <AgenticChatQuestionField
              key={field.id}
              field={field}
              answer={answers.get(field.id) ?? []}
              disabled={!isOpen}
              onAnswerChange={(answer) =>
                setAnswers((currentAnswers) => new Map(currentAnswers).set(field.id, answer))
              }
              onAnswerCommitted={(answer) => {
                if (submissionMode !== 'SubmitWhenPicked' || !isTruthy(answer.length)) {
                  return;
                }
                void chatActions.sendMessage({
                  parts: [
                    {
                      text: formatAgenticChatQuestionAnswer({
                        answers: new Map([[field.id, answer]]),
                        question,
                      }),
                      type: 'text',
                    },
                  ],
                });
              }}
            />
          ))}
          <Stack direction="row" gap={1}>
            {submissionMode === 'SubmitWhenSent' ? (
              <Button
                type="submit"
                variant="contained"
                disabled={!isOpen || !isTruthy(answerText.length)}
              >
                Send answer
              </Button>
            ) : null}
            <Button
              disabled={!isOpen}
              onClick={() => {
                void chatActions.sendMessage({ parts: [{ text: 'Skipped.', type: 'text' }] });
              }}
            >
              Skip
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
